import { useQuery, useMutation } from '@tanstack/react-query'
import { authorizedFetch } from '@/lib/api'
import { API_ORIGIN } from '../constants'
import type { PublicationStatus } from '@/hooks/use-evaluation-rubrics-admin'
import { publishedOnly } from '@/utils/skill-lexicon'

export interface Skill {
  id: string
  name: string
  status?: 'approved' | 'crowdsourced'
  publication_status?: PublicationStatus
}

const EMPTY_SKILLS: Skill[] = []

export function useSkills() {
  const fullUrl = `${API_ORIGIN}/api/skills`

  const skillsQuery = useQuery({
    queryKey: ['skills'],
    queryFn: async (): Promise<Skill[]> => {
      const urlWithSlash = fullUrl.endsWith('/') ? fullUrl : fullUrl + '/'
      const response = await fetch(urlWithSlash)
      if (!response.ok) {
        throw new Error(`Failed to fetch skills: ${response.status}`)
      }
      return publishedOnly((await response.json()) as Skill[])
    },
  })

  const createSkillMutation = useMutation({
    mutationFn: async (skillName: string): Promise<Skill> => {
      const urlWithSlash = fullUrl.endsWith('/') ? fullUrl : fullUrl + '/'
      const response = await authorizedFetch(urlWithSlash, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: skillName }),
      })
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || `Failed to create skill: ${response.status}`)
      }
      return response.json()
    },
    onSuccess: () => {
      skillsQuery.refetch()
    },
  })

  return {
    skills: skillsQuery.data ?? EMPTY_SKILLS,
    isLoading: skillsQuery.isLoading,
    error: skillsQuery.error,
    createSkill: createSkillMutation.mutate,
    createSkillAsync: createSkillMutation.mutateAsync,
    isCreatingSkill: createSkillMutation.isPending,
    createSkillError: createSkillMutation.error,
  }
}
